// The appointment_date is used as the Sort Key for the appointment dynamoDB table, and is used here on the UI
// for scheduling appointments, viewing appointments for a given day, etc. It is important for how appointments
// are handled that all appointment_date values are formatted as ISO datestrings. This service exposes various
// values and helper functions to make working with dates in this format easier across the app.

const today = new Date();
const year = today.getFullYear();
const month = (today.getMonth() + 1).toString().padStart(2, '0');
const day = today.getDate().toString().padStart(2, '0');
const hours = today.getHours().toString().padStart(2, '0');
const minutes = today.getMinutes().toString().padStart(2, '0');
const seconds = today.getSeconds().toString().padStart(2, '0');
const milliseconds = today.getMilliseconds().toString().padStart(3, '0');

export const defaultHour = '01:00:00.000';
// today's date in ISO format with the default starting hour applied
export const todayFormattedForISO = `${year}-${month}-${day}T${defaultHour}`;
// today's date in ISO format with the exact current timestamp applied
export const nowFormattedForISO = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`

// helper function that receives a date object and formats it for how the backend will expect
// to receive it. Importantly we remove the trailing "Z" character denoting UTC timezone if it is
// present, as it can cause weird timezone inconsistencies if it is left on.
export const formatDateForDB = (date: Date) => {
  const dateStringArr = date.toISOString().split('');

  if (dateStringArr[dateStringArr.length - 1] === 'Z') {
    return date.toISOString().slice(0, -1);
  }

  return date.toISOString();
};

// a function that returns the exact ISO formatted timestamp when the function was invoked
export const getCurrentISOTimestamp = () => {
  const _today = new Date();
  const _year = _today.getFullYear();
  const _month = (_today.getMonth() + 1).toString().padStart(2, '0');
  const _day = _today.getDate().toString().padStart(2, '0');
  const _hours = _today.getHours().toString().padStart(2, '0');
  const _minutes = _today.getMinutes().toString().padStart(2, '0');
  const _seconds = _today.getSeconds().toString().padStart(2, '0');
  const _milliseconds = _today.getMilliseconds().toString().padStart(3, '0');
  return `${_year}-${_month}-${_day}T${_hours}:${_minutes}:${_seconds}.${_milliseconds}`;
};

// Format date string as MM-DD-YYYY
export const formatDateMMDDYYYY = (dateString: string) => {
  const date = new Date(dateString);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = (date.getDate() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

// Format date string as MM-DD-YYYY - hh:mm A
export const formatDateWithTime = (dateString: string) => {
  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return '-';
    }

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = (date.getDate() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const formattedDate = `${month}-${day}-${year}`;

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${formattedDate} - ${hours}:${minutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

export const parseMMDDYYYYToISO = (dateString: string) => {
  if (!dateString) return '';

  const parts = dateString.split('-');
  if (parts.length !== 3) return '';

  const month = parts[0];
  const day = parts[1];
  const year = parts[2];

  const isoDate = `${year}-${month}-${day}`;

  if (!validateISODate(isoDate)) return '';

  return isoDate;
};

// Validate ISO formatted date (YYYY-MM-DD)
export const validateISODate = (dateString: string) => {
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;

  if (datePattern.test(dateString)) {
    const [year, month, day] = dateString.split('-');

    const numericYear = parseInt(year, 10);
    const numericMonth = parseInt(month, 10);
    const numericDay = parseInt(day, 10);

    if (
      numericYear >= 1000 && numericYear <= 9999 &&
      numericMonth >= 1 && numericMonth <= 12 &&
      numericDay >= 1 && numericDay <= 31
    ) {
      // Create a date object and check if it's valid
      const date = new Date(numericYear, numericMonth - 1, numericDay);
      return (
        date.getFullYear() === numericYear &&
        date.getMonth() === numericMonth - 1 &&
        date.getDate() === numericDay
      );
    }
  }

  return false;
};

// Validate MM-DD-YYYY format
export const validateMMDDYYYY = (dateString: string) => {
  const datePattern = /^\d{2}-\d{2}-\d{4}$/;

  if (datePattern.test(dateString)) {
    const [month, day, year] = dateString.split('-');

    const numericYear = parseInt(year, 10);
    const numericMonth = parseInt(month, 10);
    const numericDay = parseInt(day, 10);

    if (
      numericYear >= 1000 && numericYear <= 9999 &&
      numericMonth >= 1 && numericMonth <= 12 &&
      numericDay >= 1 && numericDay <= 31
    ) {
      // Create a date object and check if it's valid
      const date = new Date(numericYear, numericMonth - 1, numericDay);
      return (
        date.getFullYear() === numericYear &&
        date.getMonth() === numericMonth - 1 &&
        date.getDate() === numericDay
      );
    }
  }

  return false;
};